import React, { useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button, Card } from 'react-bootstrap';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import useCapability from '@/shared/hooks/useCapability';
import { getPageContext } from '@/shared/utils/chatWidgetUtils';

import useWidgetChat from '../hooks/useWidgetChat';

import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import './ChatWidget.css';

export default function ChatWidget() {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const inputRef = useRef(null);
    const location = useLocation();
    const { user } = useAuth();
    const { ecosystem, isPlatform } = useEcosystem();
    // Per-tenant capability gate. Hidden entirely (matches the existing
    // `pageContext.visible` pattern of "render nothing") when the active
    // tenant has turned off the aiAssistant flag. Read AFTER all hooks
    // above so hook order is stable; the actual gate happens together with
    // the pageContext.visible check below.
    const aiEnabled = useCapability('aiAssistant');

    const {
        messages,
        loading,
        messagesEndRef,
        threadId,
        connect,
        disconnect,
        send,
        promote,
    } = useWidgetChat();

    const pageContext = getPageContext(location.pathname, ecosystem?.name);

    // Don't render the widget: on the platform apex (there is no RILE-level
    // agent to talk to yet, and the aiAssistant capability defaults true with
    // no apex flags, which would otherwise surface the bubble), on hidden
    // pages, or when the active tenant has disabled the aiAssistant capability.
    if (isPlatform || !aiEnabled || !pageContext.visible) return null;

    const handleToggle = () => {
        if (!open) {
            setOpen(true);
            // Lazy-connect when opening (only if authenticated)
            if (user) {
                connect();
            }
        } else {
            setOpen(false);
            disconnect();
        }
    };

    const handleSend = (e) => {
        e.preventDefault();
        if (!input.trim()) return;
        send(input);
        setInput('');
    };

    // Collapsed state: show floating button with prompt bubble
    if (!open) {
        return (
            <div className="chat-widget-container">
                <div className="chat-widget-prompt bg-white text-muted">
                    {pageContext.promptText}
                </div>
                <Button
                    className="chat-widget-button btn-primary"
                    onClick={handleToggle}
                    aria-label="Open chat assistant"
                >
                    {/* Bootstrap chat-dots icon */}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                    >
                        <path
                            d={
                                'M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0z' +
                                'm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z' +
                                'm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z'
                            }
                        />
                        <path
                            d={
                                'm2.165 15.803.02-.004c1.83-.363 ' +
                                '2.948-.842 3.468-1.105A9.06 9.06 ' +
                                '0 0 0 8 15c4.418 0 8-3.134 8-7s' +
                                '-3.582-7-8-7-8 3.134-8 7c0 1.76' +
                                '.743 3.37 1.97 4.6a10.437 10.437 ' +
                                '0 0 1-.524 2.318l-.003.011a10.722 ' +
                                '10.722 0 0 1-.244.637c-.079.186' +
                                '.074.394.272.362a21.673 21.673 ' +
                                '0 0 0 .693-.125zm.8-3.108a1 1 0 ' +
                                '0 0-.287-.801C1.618 10.83 1 9.468 ' +
                                '1 8c0-3.192 3.004-6 7-6s7 2.808 ' +
                                '7 6c0 3.193-3.004 6-7 6a8.06 8.06 ' +
                                '0 0 1-2.088-.272 1 1 0 0 0-.711' +
                                '.074c-.387.196-1.24.57-2.634.893' +
                                'a10.97 10.97 0 0 0 .398-2z'
                            }
                        />
                    </svg>
                </Button>
            </div>
        );
    }

    // Expanded state: show chat panel
    return (
        <div className="chat-widget-container">
            <Card className="chat-widget-panel border-0">
                {/* Header */}
                <div
                    className={
                        'chat-widget-header bg-primary text-white ' +
                        'px-3 pb-3 pt-4 d-flex justify-content-between ' +
                        'align-items-center'
                    }
                >
                    <div>
                        <h6 className="mb-0 fw-bold text-white">Aspen</h6>
                        <small className="text-white opacity-75">
                            Ecosystem Assistant
                        </small>
                    </div>
                    <Button
                        variant="link"
                        className="text-white p-0"
                        onClick={handleToggle}
                        aria-label="Close chat assistant"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                        >
                            <path
                                d={
                                    'M4.646 4.646a.5.5 0 0 1 .708 ' +
                                    '0L8 7.293l2.646-2.647a.5.5 0 ' +
                                    '0 1 .708.708L8.707 8l2.647 ' +
                                    '2.646a.5.5 0 0 1-.708.708L8 ' +
                                    '8.707l-2.646 2.647a.5.5 0 0 ' +
                                    '1-.708-.708L7.293 8 4.646 ' +
                                    '5.354a.5.5 0 0 1 0-.708z'
                                }
                            />
                        </svg>
                    </Button>
                </div>

                {/* Body: either sign-in prompt or chat */}
                {!user ? (
                    <div className="chat-widget-signin p-4">
                        <div className="text-center">
                            <p className="text-muted mb-3">
                                Sign in to chat with Aspen, the ecosystem
                                assistant.
                            </p>
                            <Button
                                as={Link}
                                to="/login"
                                variant="primary"
                                className="rounded-pill"
                                onClick={() => setOpen(false)}
                            >
                                Sign In to Chat
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="chat-widget-messages bg-light p-3">
                            <ChatMessages
                                messages={messages}
                                loading={loading}
                                loadingLabel="Thinking..."
                                messagesEndRef={messagesEndRef}
                            />
                        </div>
                        <div className="chat-widget-input bg-white p-2 border-top">
                            <ChatInput
                                input={input}
                                onInputChange={setInput}
                                onSend={handleSend}
                                loading={loading}
                                placeholder="Ask a question..."
                                inputRef={inputRef}
                            />
                        </div>
                        {messages.some((m) => m.sender === 'user') &&
                            threadId && (
                                <div className="chat-widget-continue text-center py-1 border-top">
                                    <Link
                                        to={`/assistant/rile?thread=${encodeURIComponent(threadId)}`}
                                        className="small text-primary text-decoration-none"
                                        onClick={() => {
                                            promote();
                                            setOpen(false);
                                            disconnect();
                                        }}
                                    >
                                        Continue in Assistant &rarr;
                                    </Link>
                                </div>
                            )}
                    </>
                )}
            </Card>
        </div>
    );
}
